<template>
  <div class="auth-wrapper" :class="{excel: isExcel}">
    <GraphiteModal
      :visible="showCampaignDuplicateConnection"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      :hide-header-close="true"
      :title="$t('sign_in.campaign_duplicate_connection')"
    >
      {{ $t("sign_in.campaign_duplicate_connection_body", {entity: invitingEntityName}) }}
      <template v-slot:modal-footer>
        <div class="w-100">
          <GraphiteButton size="sm" variant="primary" @click="closeCampaignDuplicateConnection">
            {{ $t("actions.close") }}
          </GraphiteButton>
        </div>
      </template>
    </GraphiteModal>
    <GraphiteModal
      v-model:visible="showBrowserIncompatibleModal"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      :hide-header-close="true"
      :title="$t('sign_in.browser_incomp_title')"
    >
      {{ $t("sign_in.browser_incomp_body") }}
      <template v-slot:modal-footer>
        <div class="w-100">
          <GraphiteButton size="sm" variant="primary" @click="showBrowserIncompatibleModal = false">
            {{ $t("actions.close") }}
          </GraphiteButton>
        </div>
      </template>
    </GraphiteModal>
    <div class="left-side" :class="{middle}" v-if="!isExcel">
      <slot name="leftSide">
        <div class="content-wrap">
          <div class="main-img">
            <img src="/static/g-logo-white.png" />
          </div>
          <div class="tag-line">
            <div class="line-1">{{ $t("tagline1") }}</div>
            <div class="line-2">{{ $t("tagline2") }}</div>
          </div>
        </div>
      </slot>
    </div>
    <div class="right-side" v-if="!middle">
      <ErrorMessage :error="error" />
      <div class="right-content">
        <slot name="default" />
      </div>
      <div class="i18n-footer flex justify-content-end">
        <div
          class="mr-3 link-alike"
          v-for="lang in languageOptionsShort"
          @click="changeLanguage(lang.value)"
          :key="lang.value"
          :tabindex="0"
        >
          {{ lang.text }}
        </div>
        <NavDropdown :direction="NavDirection.UP">
          <template #toggle>
            <span class="language-nav-item link-alike">
              <font-awesome-icon class="i18n-icon mr-1" :icon="['far', 'globe-americas']" />
              {{ $t("sign_in.show_more") }}
            </span>
          </template>
          <template #items>
            <div class="flex">
              <div class="first">
                <NavDropdownItem v-for="lang in langOptionsFirst" @click="changeLanguage(lang.value)" :key="lang.value">
                  {{ lang.text }}
                </NavDropdownItem>
              </div>
              <div class="second">
                <NavDropdownItem
                  v-for="lang in langOptionsSecond"
                  @click="changeLanguage(lang.value)"
                  :key="lang.value"
                >
                  {{ lang.text }}
                </NavDropdownItem>
              </div>
            </div>
          </template>
        </NavDropdown>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {getStore} from "@/composables/get.store";
import {monitorElementForWrapping} from "@/composables/layoutUtils";
import {changeLanguage} from "@/composables/menu";
import {NavDirection} from "@/composables/menu/enums";
import {i18n, localeList} from "@/i18n";
import globalLogger from "@/logging";
import {isExcel} from "@/utils/excel";
import {Locale} from "pg-isomorphic/enums";
import type {ComputedRef, Ref} from "vue";
import {computed, defineAsyncComponent, onMounted, ref} from "vue";

const ErrorMessage = defineAsyncComponent(() => import("@/components/shared/ErrorMessage.vue"));
const NavDropdown = defineAsyncComponent(() => import("@/components/menu/shared/nav-dropdown/NavDropdown.vue"));
const NavDropdownItem = defineAsyncComponent(() => import("@/components/menu/shared/nav-dropdown/NavDropdownItem.vue"));

const logger = globalLogger.getLogger("AuthWrapper");

const POPULAR_LANGUAGES: Locale[] = [
  Locale.EN_US,
  Locale.ZH_CN,
  Locale.JA_JP,
  Locale.DE_DE,
  Locale.ES_ES,
  Locale.FR_FR,
  Locale.ZH_TW,
  Locale.PT_BR,
];

const props = defineProps({
  error: {
    type: String,
    default: "",
  },
  showCampaignDuplicateConnection: {
    type: Boolean,
    default: false,
  },
  invitingEntityName: {
    type: String,
    default: "",
  },
  middle: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  closeDuplicateCampaign: [];
  wrapped: [];
}>();

// DATA
const showBrowserIncompatibleModal: Ref<boolean> = ref(false);
const authWrapperElement: Ref<Element> = ref(null);

// COMPUTED
const supportsHistoryApi: ComputedRef<boolean> = computed(() => {
  return !!(window.history && window.history.pushState);
});

const language = computed(() => getStore().state.language || window.localStorage.getItem("language") || i18n.locale);

const languageOptionsShort = computed(() => makeLangOptions(POPULAR_LANGUAGES, language.value, true));
const languageOptions = computed(() => makeLangOptions(Object.keys(localeList)));

const langOptionsFirst = computed(() => languageOptions.value.slice(0, languageOptions.value.length / 2));
const langOptionsSecond = computed(() => languageOptions.value.slice(languageOptions.value.length / 2));

// METHODS
const closeCampaignDuplicateConnection = () => {
  emit("closeDuplicateCampaign");
};

const makeLangOptions = (locales: Locale[], current?: string, names?: boolean) => {
  logger.trace(() => `makeLangOptions ${current} ${names}`);
  return locales
    .filter((l) => (current ? l !== current : true))
    .map((key) => ({
      text: (names ? localeList[key]?.name : localeList[key]?.displayName) || key,
      value: key,
    }));
};

monitorElementForWrapping(authWrapperElement, "div.auth-wrapper", "auth-wrapper-wrapped", emit);

onMounted(() => {
  // Check for browser compatibility
  if (!supportsHistoryApi.value) {
    showBrowserIncompatibleModal.value = true;
  }
});
</script>

<style lang="less" scoped>
@import "@/less/global.less";

.auth-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-items: center;
  width: 100%;
  min-height: calc(100vh - @menuHeight);

  &.middle {
    margin: 0 auto;
    justify-content: center;
  }

  &.auth-wrapper-wrapped {
    min-height: auto;
  }

  .left-side {
    display: flex;
    flex-direction: column;
    flex-basis: 40%;
    flex-grow: 1;
    justify-items: center;
    align-items: center;
    background-color: @publicPagesBg;
    color: @white;
    padding: ~"min(85px, 10%)" 25px;

    &.middle {
      max-width: 580px;
    }

    .content-wrap {
      .main-img {
        img {
          width: 121px;
          height: 121px;
        }
      }

      .tag-line {
        font-size: @font20;
        margin-top: 32px;

        > div {
          white-space: nowrap;
        }
      }
    }

    &.auth-wrapper-wrapped {
      padding: 23px;

      .content-wrap {
        margin: 0 60px; // keep similar size to avoid "un-wrapping" because width changed
        .main-img {
          img {
            width: 63px;
            height: 63px;
          }
        }
        .tag-line {
          font-size: @font12;
          margin-top: 12px;
        }
      }
    }
  }

  .right-side {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-basis: 60%;
    padding: ~"min(85px, 10%)" ~"min(85px, 5%)";
    padding-bottom: 15px;

    .i18n-footer {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-top: 30px;
      font-size: @font14;
    }
  }
}
</style>

<style lang="less">
@import "@/less/global.less";

.auth-wrapper {
  .right-side {
    .i18n-footer {
      .nav-dropdown-wrapper {
        z-index: 998; // must be below top menu
        height: unset;

        .nav-dropdown-toggle-icon {
          border-left: none;
          padding-left: 0;
        }
      }
    }
  }
}
</style>
